/**
 * Parts Master data table — universal, schedule, and EXTRA_COLUMNS rows.
 *
 * Columns render in the user's persisted `order`. Sticky-left columns
 * (`part_number`, `description`) are always rendered first physically — their
 * position in the order list is ignored. Hidden columns are skipped; the
 * `showSchedule` / `showExtras` flags still gate the schedule and extra
 * column groups so tenants without those features never see them at all.
 */
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import type { Part } from '../../lib/api/types/parts';
import { EXTRA_COLUMNS, type ExtraColumn } from '../../config/parts-master-columns';

const STICKY_COL_1 = 'sticky left-0 z-10 bg-background [tr:hover_&]:bg-muted/50';
const STICKY_COL_2 = 'sticky left-[100px] z-10 bg-background [tr:hover_&]:bg-muted/50';
const STICKY_HEAD_1 = 'sticky left-0 z-20 bg-background';
const STICKY_HEAD_2 = 'sticky left-[100px] z-20 bg-background';

export type SortDir = 'asc' | 'desc' | null;

interface SortableHeadProps {
  label: string;
  sortKey: string;
  active: string | null;
  dir: SortDir;
  onToggle: (key: string) => void;
  className?: string;
  align?: 'left' | 'right';
}

function SortableHead({ label, sortKey, active, dir, onToggle, className, align = 'left' }: SortableHeadProps) {
  const isActive = active === sortKey && dir !== null;
  const Icon = isActive ? (dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  const justify = align === 'right' ? 'justify-end' : 'justify-start';
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={`flex items-center gap-1 w-full ${justify} hover:text-foreground transition-colors`}
      >
        <span className="truncate">{label}</span>
        <Icon className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-foreground' : 'text-muted-foreground/60'}`} />
      </button>
    </TableHead>
  );
}

interface PartsMasterTableProps {
  parts: Part[];
  sortField: string | null;
  sortDir: SortDir;
  onToggleSort: (key: string) => void;
  showExtras: boolean;
  showSchedule: boolean;
  visible: Set<string>;
  /** User's persisted column order (sticky-left ids included at the head). */
  order: string[];
  formatDate: (value: string) => string;
}

/** Lookup: column id → its EXTRA_COLUMNS registry entry (when extras apply). */
function buildExtraIndex(): Record<string, ExtraColumn> {
  const map: Record<string, ExtraColumn> = {};
  for (const col of EXTRA_COLUMNS) map[col.key as string] = col;
  return map;
}

const EXTRA_INDEX = buildExtraIndex();

/** Universal + schedule column ids the table knows how to render natively. */
const NON_EXTRA_IDS = new Set<string>([
  'part_number',
  'description',
  'commodity_group_name',
  'commodity_type_name',
  'uom',
  'is_active',
  'updated_at',
  'schedule_firm',
  'schedule_active',
]);

/** Sticky-left ids — these always render first regardless of order. */
const STICKY_IDS = new Set<string>(['part_number', 'description']);

interface NonExtraHeadProps {
  id: string;
  sortField: string | null;
  sortDir: SortDir;
  onToggleSort: (key: string) => void;
}

function NonExtraHead({ id, sortField, sortDir, onToggleSort }: NonExtraHeadProps) {
  switch (id) {
    case 'part_number':
      return (
        <SortableHead
          label="Part Number" sortKey="part_number" active={sortField} dir={sortDir}
          onToggle={onToggleSort} className={`w-[100px] ${STICKY_HEAD_1}`}
        />
      );
    case 'description':
      return (
        <SortableHead
          label="Description" sortKey="description" active={sortField} dir={sortDir}
          onToggle={onToggleSort} className={`w-[320px] ${STICKY_HEAD_2}`}
        />
      );
    case 'commodity_group_name':
      return (
        <SortableHead
          label="Commodity Group" sortKey="commodity_group__name" active={sortField} dir={sortDir}
          onToggle={onToggleSort} className="w-[140px]"
        />
      );
    case 'commodity_type_name':
      return (
        <SortableHead
          label="Commodity Type" sortKey="commodity_type__name" active={sortField} dir={sortDir}
          onToggle={onToggleSort} className="w-[140px]"
        />
      );
    case 'uom':
      return (
        <SortableHead
          label="UOM" sortKey="uom" active={sortField} dir={sortDir}
          onToggle={onToggleSort} className="w-[70px]"
        />
      );
    case 'schedule_firm':
      return <TableHead className="w-[110px] text-right">Firm Days</TableHead>;
    case 'schedule_active':
      return <TableHead className="w-[120px]">Schedule</TableHead>;
    case 'is_active':
      return (
        <SortableHead
          label="Status" sortKey="is_active" active={sortField} dir={sortDir}
          onToggle={onToggleSort} className="w-[80px]"
        />
      );
    case 'updated_at':
      return (
        <SortableHead
          label="Updated" sortKey="updated_at" active={sortField} dir={sortDir}
          onToggle={onToggleSort} className="w-[95px]"
        />
      );
    default:
      return null;
  }
}

interface NonExtraCellProps {
  id: string;
  part: Part;
  formatDate: (value: string) => string;
}

function NonExtraCell({ id, part, formatDate }: NonExtraCellProps) {
  switch (id) {
    case 'part_number':
      return (
        <TableCell className={`font-mono font-medium ${STICKY_COL_1}`}>
          {part.part_number}
        </TableCell>
      );
    case 'description':
      return (
        <TableCell className={`max-w-[320px] whitespace-normal break-words ${STICKY_COL_2}`}>
          {part.description}
        </TableCell>
      );
    case 'commodity_group_name':
      return <TableCell>{part.commodity_group_name || '-'}</TableCell>;
    case 'commodity_type_name':
      return <TableCell>{part.commodity_type_name || '-'}</TableCell>;
    case 'uom':
      return <TableCell>{part.uom}</TableCell>;
    case 'schedule_firm':
      return (
        <TableCell className="text-right tabular-nums">
          {part.schedule_firm ?? '—'}
        </TableCell>
      );
    case 'schedule_active':
      return (
        <TableCell>
          {part.schedule_active === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <Badge variant={part.schedule_active ? 'default' : 'secondary'}>
              {part.schedule_active ? 'Active' : 'Inactive'}
            </Badge>
          )}
        </TableCell>
      );
    case 'is_active':
      return (
        <TableCell>
          <Badge variant={part.is_active ? 'default' : 'secondary'}>
            {part.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </TableCell>
      );
    case 'updated_at':
      return (
        <TableCell className="text-muted-foreground text-sm">
          {formatDate(part.updated_at)}
        </TableCell>
      );
    default:
      return null;
  }
}

/**
 * Render the Parts Master table.
 *
 * Column visibility comes from `visible`; column order comes from `order`.
 * Sticky-left ids always render first physically — the rest follow whatever
 * the user has dragged into place.
 */
export default function PartsMasterTable({
  parts,
  sortField,
  sortDir,
  onToggleSort,
  showExtras,
  showSchedule,
  visible,
  order,
  formatDate,
}: PartsMasterTableProps) {
  const v = (id: string) => visible.has(id);

  // Resolve the renderable id list:
  // - Always start with the sticky-left ids in their declared order (visibility
  //   filtered, but they're forced-visible at the hook layer).
  // - Then walk the user's `order`, skipping sticky ids (already placed),
  //   hidden ids, schedule columns when `showSchedule` is false, and extras
  //   when `showExtras` is false.
  // - Append any registry id missing from `order` (transitional safety).
  const renderableIds: string[] = [];
  const seen = new Set<string>();

  // Sticky head.
  for (const id of ['part_number', 'description']) {
    if (v(id)) {
      renderableIds.push(id);
      seen.add(id);
    }
  }

  const isAllowed = (id: string): boolean => {
    if (!v(id)) return false;
    if (id === 'schedule_firm' || id === 'schedule_active') return showSchedule;
    if (NON_EXTRA_IDS.has(id)) return true;
    // Otherwise, treat as an extras id.
    return showExtras && id in EXTRA_INDEX;
  };

  for (const id of order) {
    if (seen.has(id)) continue;
    if (STICKY_IDS.has(id)) continue;
    if (isAllowed(id)) {
      renderableIds.push(id);
      seen.add(id);
    }
  }

  // Safety: any column the order array hasn't seen yet (registry just added).
  NON_EXTRA_IDS.forEach((id) => {
    if (!seen.has(id) && isAllowed(id)) {
      renderableIds.push(id);
      seen.add(id);
    }
  });
  if (showExtras) {
    for (const col of EXTRA_COLUMNS) {
      const id = col.key as string;
      if (!seen.has(id) && v(id)) {
        renderableIds.push(id);
        seen.add(id);
      }
    }
  }

  return (
    <Table wrapperClassName="max-h-[calc(100vh-380px)]">
      <TableHeader className="sticky top-0 z-20 bg-background shadow-[0_1px_0_0_hsl(var(--border))]">
        <TableRow>
          {renderableIds.map((id) => {
            if (NON_EXTRA_IDS.has(id)) {
              return (
                <NonExtraHead
                  key={id}
                  id={id}
                  sortField={sortField}
                  sortDir={sortDir}
                  onToggleSort={onToggleSort}
                />
              );
            }
            const col = EXTRA_INDEX[id];
            if (!col) return null;
            return (
              <SortableHead
                key={id}
                label={col.label}
                sortKey={col.sortKey ?? (col.key as string)}
                active={sortField}
                dir={sortDir}
                onToggle={onToggleSort}
                className={col.numeric ? 'text-right' : col.className}
                align={col.numeric ? 'right' : 'left'}
              />
            );
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {parts.map((part) => (
          <TableRow key={part.id}>
            {renderableIds.map((id) => {
              if (NON_EXTRA_IDS.has(id)) {
                return (
                  <NonExtraCell
                    key={id}
                    id={id}
                    part={part}
                    formatDate={formatDate}
                  />
                );
              }
              const col = EXTRA_INDEX[id];
              if (!col) return null;
              const value = part[col.key];
              const formatted = col.format
                ? col.format(value)
                : (value === null || value === undefined || value === '')
                  ? '—'
                  : String(value);
              return (
                <TableCell
                  key={id}
                  className={col.numeric ? 'text-right tabular-nums' : col.className}
                >
                  {formatted}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
